<template>
  <div class="flex h-screen">
    <div class="my-auto w-full">
      <div v-if="preview">
        <div class="grid grid-cols-8">
          <div
            class="relative col-start-3 col-span-4 w-full"
            style="aspect-ratio: 16 / 9;"
          >
            <HiPlayer />
            <div class="flex justify-end mt-5 preview-actions">
              <button
                class="preview-action bg-red-400 hover:bg-rose-500 text-white"
                title="Back"
                aria-label="Back"
                @click="reset"
              >
                <ArrowLeftIcon />
              </button>
              <button
                class="preview-action bg-gray-victor hover:bg-gray-victor/80 text-white"
                :class="{'bg-gray-400 hover:bg-gray-400 cursor-not-allowed': published}"
                :disabled="published"
                title="Social Publishing"
                aria-label="Social Publishing"
                @click="openSocialPublishing"
              >
                <ShareIcon />
              </button>
              <button
                class="preview-action bg-gray-victor hover:bg-gray-victor/80 text-white"
                title="Download"
                aria-label="Download"
                @click="downloadVideo"
              >
                <DownloadIcon />
              </button>
              <button
                class="preview-action bg-green-450 hover:bg-green-450/80 text-white"
                :class="{'bg-gray-400 hover:bg-gray-400 cursor-not-allowed': published}"
                :disabled="published"
                title="Ingest"
                aria-label="Ingest"
                @click="publish"
              >
                <IngestIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-center"
      >
        <div
          class="inline-flex items-center px-4 py-2 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-red-400 hover:bg-rose-500 focus:border-rose-700 active:bg-rose-700 transition ease-in-out duration-150 cursor-not-allowed"
        >
          <svg
            class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span v-text="capitalize"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import router from "../router"
import {createNamespacedHelpers} from "vuex"
import VideoPlayer from '../components/VideoPlayer'
import HiPlayer from "@/components/Player/HiPlayer.vue";
import store from "@/vuex/store";
import IngestIcon from "@/components/icons/IngestIcon.vue";
import ArrowLeftIcon from "@/components/icons/ArrowLeftIcon.vue";
import ShareIcon from "@/components/icons/ShareIcon.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";

const {mapMutations} = createNamespacedHelpers('video')
const {mapState: editorState} = createNamespacedHelpers('editor')
const {mapState: liveEditState} = createNamespacedHelpers('live')
const {mapState: webSocketState} = createNamespacedHelpers('webSocket')
const {mapState: videoSeatState} = createNamespacedHelpers('videoSeat')
const {mapState: streamSeatState} = createNamespacedHelpers('streamSeat')
const {mapActions: mapWindowActions} = createNamespacedHelpers('window')
const {mapActions: mapVideoActions} = createNamespacedHelpers('videoSeat')
const {mapActions: streamSeatActions} = createNamespacedHelpers('streamSeat')
const {mapActions: liveEditorActions} = createNamespacedHelpers('live')
const {mapMutations: webSocketMutations} = createNamespacedHelpers('webSocket')


export default {
  name: "Preview",
  components: {
    HiPlayer,
    VideoPlayer,
    IngestIcon,
    ArrowLeftIcon,
    ShareIcon,
    DownloadIcon,
  },
  data: function () {
    return {
      status: 'Processing',
      preview: false,
      video: null,
      pingInterval: null,
      published: false
    }
  },
  computed: {
    ...webSocketState({
      connection: state => state.connection,
      isSocketConnectionEstablished: state => state.isSocketConnectionEstablished,
    }),
    ...videoSeatState({
      video_seat_id: state => state.video_seat_id
    }),
    ...streamSeatState({
      stream_video_id: state => state.stream_video_id
    }),
    ...editorState({
      is_stream: state => state.is_stream
    }),
    ...liveEditState({
      is_live: state => state.is_live,
      live_id: state => state.live_id,
      live_seat_id: state => state.stream.stream_seat_id
    }),
    capitalize() {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
  },
  created: function () {
    // websocket connection
    this.initConnection()

    let self = this
    this.connection.onopen = function () {
      self.pingSocketConnection()
      if (self.is_live) {
        self.connection.send(JSON.stringify({request: 'SUBSCRIBE_LIVE_EDITOR_BY_ID', data: {id: self.live_id}}));
      } else if (!self.is_stream) {
        self.connection.send(JSON.stringify({request: 'SUBSCRIBE_BY_ID', data: {id: self.video_seat_id}}));
      } else {
        self.connection.send(JSON.stringify({request: 'SUBSCRIBE_STREAM_BY_ID', data: {id: self.stream_video_id}}));
      }
    }

    this.connection.onmessage = (event) => {
      this.onSocketMessage(event)
    }

    this.connection.onclose = () => {
      console.log('connection is closed')
    }

    // add ping to connection just to keep it alive every 20 seconds


  },
  mounted() {
    this.$store.commit('live/setStream', null)
    this.$store.commit('live/setLoading', false)
  },
  methods: {
    ...mapMutations({
      setVideo: 'setVideo'
    }),
    ...webSocketMutations({
      initConnection: 'init'
    }),
    ...mapWindowActions({
      windowPostDeleteMessage: 'postDeleteMessage',
      windowPublishMessage: 'publish',
      windowSocialPublishingMessage: 'socialPublishing'
    }),
    ...mapVideoActions({
      cancelVideoRecord: 'cancelVideoRecord',
      publishRecord: 'publishRecord',
      getRecordById: 'getRecordById',
    }),
    ...liveEditorActions({
      cancelLiveEditorRecord: 'cancelRecord',
      publishLiveSeatRecord: 'publishRecord',
    }),
    ...streamSeatActions({
      cancelStreamRecord: 'cancelRecord',
      seatPublishRecord: 'publishRecord'
    }),
    onSocketMessage: function (event) {
      let json = event.data
      json = JSON.parse(json)
      switch (json.request) {
        case 'CHANGE_STATUS':
        case 'UPDATE_STATUS':
          this.status = json.data.status
          break

        case 'SYN_SUBSCRIBE':
          // it means that server received the subscription
          // send back the confirmation that client received the SYN
          // this.connection.send(JSON.stringify({request:'SYN', data: {video_id: json.data.video_id}}));
          // also go to the preview page where we keep track of our video by job id
          break

        case 'PREVIEW':
          this.stopPingConnection()
          this.preview = true
          this.video = json.data.video
          this.setVideo({
            url: this.video.output || this.video.output_url,
            duration: 0, // for now, we don't need this option
            type: 'video/mp4'
          })
          // self.callRemoteVideos(json.data.url)
          break

        case 'EXCEPTION':
          // go back to editor with error
          this.stopPingConnection()
          this.$notify({
            title: 'Error',
            type: 'error',
            text: (json.data?.message || 'Error while converting video'),
            message: (json.data?.message || 'Error while converting video'),
            offset: 100
          })
          if(this.is_live) {
            router.push({name: 'live-editor'})
            return
          }
          if (this.is_stream) {
            router.push({name: 'StreamHome'})
          } else {
            router.push({name: 'home'})
          }
      }
    },

    reset: function () {
      // cancel video
      if (this.is_live) {
        this.cancelLiveEditorRecord().then((res) => {
          if (res.data.success) {
            router.push({name: 'live-editor'})
          } else {
            throw new Error('Something went wrong')
          }
        }).catch((error) => {
          this.$notify({
            title: 'Error',
            type: 'error',
            text: error.message,
            message: error.message,
            offset: 100
          })
        })
        return
      }
      if (!this.is_stream) {
        this.cancelVideoRecord().then((response) => {
          const data = response.data
          if (data.success) {
            this.setVideo(JSON.parse(this.video.inputs))
            router.push({name: 'home'})
          } else {
            throw new Error('Something went wrong')
          }
        }).catch((error) => {
          this.$notify({
            title: 'Error',
            type: 'error',
            text: error.message,
            message: error.message,
            offset: 100
          })
        })
      } else {
        this.cancelStreamRecord().then(() => {
          this.setVideo({
            duration: this.video.duration,
            url: this.video.video_url,
            type: 'video/mp4',
            fast_url: this.video.video_url
          })
          router.push({name: 'StreamHome'})
        }).catch((error) => {
          this.$notify({
            title: 'Error',
            type: 'error',
            text: error.message,
            message: error.message,
            offset: 100
          })
        })
      }
    },

    publish: function () {
      this.published = true
      store.commit('live/setLoading', true)
      if (this.is_live) {
        this.publishLiveSeatRecord().then(() => {
          this.windowPublishMessage()
        })
        return
      }
      if (this.is_stream) {
        this.seatPublishRecord().then(() => {
          this.windowPublishMessage()
        })
      } else {
        this.publishRecord().then(() => {
          this.windowPublishMessage()
        })
      }
    },

    openSocialPublishing: function () {
      if (!this.video || !(this.video.output || this.video.output_url)) {
        this.$notify({
          title: 'Warning',
          type: 'warning',
          text: 'Prepared video is not available yet',
          message: 'Prepared video is not available yet',
          offset: 100
        })
        return
      }

      this.windowSocialPublishingMessage()
    },

    downloadVideo: function () {
      const outputUrl = this.video && (this.video.output || this.video.output_url)
      if (!outputUrl) {
        this.$notify({
          title: 'Warning',
          type: 'warning',
          text: 'Prepared video is not available yet',
          message: 'Prepared video is not available yet',
          offset: 100
        })
        return
      }

      try {
        const source = new URL(outputUrl, window.location.origin)
        const filename = decodeURIComponent(source.pathname.split('/').pop() || '')
        if (!/^[a-zA-Z0-9_-]+\.mp4$/i.test(filename)) {
          throw new Error('Prepared video filename is invalid')
        }

        const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin
        const downloadUrl = new URL(`/api/shared/download-output/${encodeURIComponent(filename)}`, baseUrl)
        const link = document.createElement('a')
        link.href = downloadUrl.toString()
        link.download = filename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        this.$notify({
          title: 'Error',
          type: 'error',
          text: error.message || 'Failed to download prepared video',
          message: error.message || 'Failed to download prepared video',
          offset: 100
        })
      }
    },

    pingSocketConnection: function () {
      const local = this
      if (this.pingInterval) {
        clearInterval(this.pingInterval)
      }
      this.pingInterval = setInterval(() => {
        local.connection.send(JSON.stringify({request: 'PING', data: {}}))
      }, 20000)
    },

    stopPingConnection: function () {
      if (this.pingInterval) {
        clearInterval(this.pingInterval)
      }
    }
  },
}
</script>

<style scoped>
.preview-actions {
  gap: 8px;
}

.preview-action {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  transition: background-color 150ms ease, transform 150ms ease;
  width: 40px;
}

.preview-action:not(:disabled):active {
  transform: scale(0.96);
}

.preview-action:focus-visible {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
  outline: none;
}

.preview-action svg {
  height: 22px;
  width: 22px;
}
</style>
